/** @format */
import isPropValid from '@emotion/is-prop-valid';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { useClickOutside } from '@mpb-com/feather/hooks/use-click-outside';
import { animated, useSpring } from '@react-spring/web';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent, ReactNode, useRef } from 'react';
import styled from 'styled-components';

import CloseIcon from '@toucan/client/global/icons/close.svg';

/* eslint-disable-next-line boundaries/element-types -- This should really be in the components folder for the _app page,
 but thats not currently possible */
import { useKeyPress } from '@toucan/client/shared/hooks/use-key-press';

import { styles } from './drawer.styles';

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum DrawerOpenDirection {
    LEFT = 'left',
    RIGHT = 'right',
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
export interface DrawerProps {
    /**
     * Child elements
     */
    children?: ReactNode;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * Open status of drawer
     * @default false
     */
    isOpen?: boolean;

    /**
     * Handler for when the drawer closes
     */
    onClose?: () => void;

    /**
     * Which position does the drawer occupy
     */
    openDirection?: DrawerOpenDirection;

    /**
     * Skip animations and close immediately
     * @default false
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    immediate?: boolean;
}

const StyledDrawer = styled(animated.aside).withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const Drawer: FunctionComponent<DrawerProps> = ({
    children,
    isOpen = false,
    onClose,
    openDirection = DrawerOpenDirection.LEFT,
    immediate = false,
    ...otherProps
}: DrawerProps) => {
    const { t } = useTranslation();
    const bodyRef = useRef(
        typeof document !== 'undefined' ? document.body : null
    );
    const drawerRef = useRef(null);

    const animationProps = useSpring({
        transform: isOpen
            ? `translate3d(0%, 0, 0)`
            : `translate3d(${
                  openDirection === DrawerOpenDirection.LEFT ? '-100%' : '100%'
              }, 0, 0)`,
        [openDirection]: '0',
        immediate,
    });

    /**
     * Handle an escape keypress, invoking the onClose prop if the drawer
     * is currently open.
     */
    useKeyPress(bodyRef.current, ['Escape', 'Esc'], () => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- Legacy code: do not use as an example.
        isOpen && onClose?.();
    });

    /**
     * Handle clicks outside the drawer, invoking the onClose prop if the drawer
     * is currently open.
     */
    useClickOutside(drawerRef, () => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- Legacy code: do not use as an example.
        isOpen && onClose?.();
    });

    return (
        <StyledDrawer {...otherProps} style={animationProps} ref={drawerRef}>
            <div className="header" data-testid="drawer-header">
                <Icon
                    className="close-button"
                    aria-label={t('Close drawer')}
                    variant={IconVariant.DECORATIVE}
                    onClick={() => {
                        onClose?.();
                    }}
                    size={IconSize.S}
                    data-testid="drawer-close-button"
                >
                    <CloseIcon />
                </Icon>
            </div>
            <div className="contents" data-testid="drawer-content">
                {children}
            </div>
        </StyledDrawer>
    );
};
